import React from "react"
import Radium, { keyframes } from "higher-order-components/configured-radium/"
import { InputLabel } from "components"
import PropTypes from "prop-types"
import { invokeFuncs } from "utils/function"

/**
 * @param {String} label - Field label text
 * @param {String} value - Input value
 * @param {Number} maxLength - input maxLength property
 * @param {String} placeholder - Field placeholder text
 * @param {Boolean} disabled - disable input
 * @param {String} icon - Name of icon
 * @param {Boolean} isValid - validation status of input
 * @param {String} message - message displayed after input
 * @param {Boolean} required - input required
 * @param {Function} onChange - onChange callback of input
 * @returns {ReactElement} React Component
 */
const InputTextArea = ({
  label,
  value,
  maxLength,
  placeholder,
  disabled,
  icon,
  isValid,
  message,
  required,
  onChange,
  type,
  name,
  onBlur,
  onKeyDown,
  onFocus,
  autoComplete,
  gtmEvents
}) => {
  const valid = typeof isValid === "undefined" || isValid
  const color = valid ? "#d5d5d5" : "#bf0000"
  const textColor = valid ? "#101010" : "#bf0000"
  const autoResize = e => {
    e.target.style.height = e.target.scrollHeight + "px"
  }
  /**
   * onMouseUp and onClick was added to fix IE bugs
   * However, it slows down the typing experience as the browser now has to check for 4 events
   * we can drop those 2 event hooks when IE removed from support list
   */
  return (
    <div style={{ margin: "1em auto 1.5em", position: "relative" }}>
      <div style={{ position: "relative", color: textColor }}>
        {label && (
          <InputLabel textArea text={label} dirty={value && value.length > 0} required={required} />
        )}
        <textarea
          rows={"1"}
          ref={"input"}
          style={[
            styles.input,
            styles.border(color),
            { color: textColor },
            styles.iconPadding(icon)
          ]}
          type={type || "text"}
          name={name}
          disabled={disabled}
          required={required}
          placeholder={!label && placeholder ? placeholder : ""}
          value={value || ""}
          onKeyDown={onKeyDown}
          autoComplete={autoComplete}
          onFocus={onFocus}
          onBlur={() => {
            gtmEvents && invokeFuncs(gtmEvents)
            onBlur && onBlur()
          }}
          maxLength={maxLength}
          onChange={e => {
            onChange(e)
            autoResize(e)
          }}
          onKeyUp={e => {
            onChange(e)
            autoResize(e)
          }}
          onMouseUp={e => {
            onChange(e)
            autoResize(e)
          }}
          onClick={e => {
            onChange(e)
            autoResize(e)
          }} />
        {!valid && icon && (
          <div style={[styles.iconWrapper, styles.fadeIn]}>
            <div style={styles.icon}>{icon("error", color)}</div>
          </div>
        )}
        {icon && (
          <div style={styles.iconWrapper}>
            <div style={styles.icon}>{icon(icon, "#999999")}</div>
          </div>
        )}
      </div>
      {message && <p style={[styles.message(textColor), styles.fadeIn]}>{message}</p>}
    </div>
  )
}

InputTextArea.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string,
  maxLength: PropTypes.number,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  icon: PropTypes.string,
  isValid: PropTypes.bool,
  message: PropTypes.string,
  required: PropTypes.bool,
  onChange: PropTypes.func,
  type: PropTypes.string,
  name: PropTypes.string,
  onKeyDown: PropTypes.func,
  onBlur: PropTypes.func,
  autoComplete: PropTypes.string,
  onFocus: PropTypes.func,
  gtmEvents: PropTypes.array
}

const fadeInKeyframes = keyframes({
  from: {
    opacity: 0
  }
})

const styles = {
  fadeIn: {
    animation: "x .666s",
    animationName: fadeInKeyframes
  },
  iconPadding: icon => ({
    padding: icon ? "1.5em calc(1.4em + 1em) .75em 0" : "0 0 .5em"
  }),
  message: color => ({
    position: "absolute",
    color: color,
    fontSize: "75%",
    fontWeight: 200,
    top: "calc(100% + .5em)",
    margin: 0
  }),
  iconWrapper: {
    position: "absolute",
    right: ".5em",
    top: "50%"
  },
  icon: {
    width: "1.4em",
    height: "1.4em",
    transform: "translateY(-50%)"
  },
  input: {
    fontFamily: "inherit",
    fontWeight: 200,
    fontSize: "100%",
    background: "none",
    boxSizing: "border-box",
    width: "100%",
    outline: "none",
    overflow: "hidden",
    resize: "none",
    transition: ".2s",
    height: "26px"
  },
  border: color => ({
    borderTop: "none",
    borderLeft: "none",
    borderRight: "none",
    borderBottom: `1px solid ${color}`
  })
}

export default Radium(InputTextArea)



// WEBPACK FOOTER //
// ./src/components/form-elements/input-textarea/index.jsx